import React from 'react';
import {
  Box,
  CircularProgress,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import PrimaryActionButton from '../common/PrimaryActionButton';
import { fieldLabelSx } from '../common/formFieldStyles';
import { BRAND } from '../../theme/brandTokens';

const sectionContentPadding = { xs: 2.5, sm: 3, md: 4 };

const WAITLIST_TYPES = ['Manual', 'Auto'];
const WAITLIST_PRIORITIES = ['FirstInLine', 'HighestValue', 'NotifyAll'];

const CommunicationWaitlistPanel = ({
  settings,
  saving,
  loading,
  onFieldChange,
  onSave,
}) => {
  const { t } = useTranslation();
  const waitlistType = settings?.waitlistType || 'Manual';

  return (
    <Box sx={{ p: sectionContentPadding }}>
      <Typography variant="h6" sx={{ color: BRAND.accentText, fontWeight: 600, mb: 1 }}>
        {t('communicationSettingsPage.waitlist.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('communicationSettingsPage.waitlist.subtitle')}
      </Typography>

      <Typography variant="subtitle2" sx={{ ...fieldLabelSx, mb: 1 }}>
        {t('communicationSettingsPage.waitlist.typeLabel')}
      </Typography>
      <RadioGroup
        value={waitlistType}
        onChange={(event) => onFieldChange('waitlistType', event.target.value)}
        sx={{ mb: 2 }}
      >
        {WAITLIST_TYPES.map((value) => (
          <FormControlLabel
            key={value}
            value={value}
            disabled={saving}
            aria-label={t(`communicationSettingsPage.waitlist.type.${value}`)}
            control={<Radio sx={{ color: BRAND.accentText, '&.Mui-checked': { color: BRAND.accentText } }} />}
            label={(
              <Box>
                <Typography sx={{ fontWeight: 600 }}>{t(`communicationSettingsPage.waitlist.type.${value}`)}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t(`communicationSettingsPage.waitlist.typeHelp.${value}`)}
                </Typography>
              </Box>
            )}
          />
        ))}
      </RadioGroup>

      {waitlistType === 'Auto' ? (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ ...fieldLabelSx, mb: 1 }}>
            {t('communicationSettingsPage.waitlist.priorityLabel')}
          </Typography>
          <RadioGroup
            value={settings?.waitlistPriority || 'FirstInLine'}
            onChange={(event) => onFieldChange('waitlistPriority', event.target.value)}
          >
            {WAITLIST_PRIORITIES.map((value) => (
              <FormControlLabel
                key={value}
                value={value}
                disabled={saving}
                control={<Radio sx={{ color: BRAND.accentText, '&.Mui-checked': { color: BRAND.accentText } }} />}
                label={(
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>{t(`communicationSettingsPage.waitlist.priority.${value}`)}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t(`communicationSettingsPage.waitlist.priorityHelp.${value}`)}
                    </Typography>
                  </Box>
                )}
              />
            ))}
          </RadioGroup>
        </Box>
      ) : null}

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <PrimaryActionButton
          onClick={onSave}
          disabled={saving || loading}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {saving ? t('common.saving') : t('common.save')}
        </PrimaryActionButton>
      </Box>
    </Box>
  );
};

export default CommunicationWaitlistPanel;
